import React, { useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ThankYou from './pages/ThankYou';
import Formations from './pages/Formations';
import Blog from './pages/Blog';
import BlogArticle from './pages/BlogArticle';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminEditArticle from './pages/AdminEditArticle';
import AdminCreateArticle from './pages/AdminCreateArticle';
import ContactRedirect from './pages/ContactRedirect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function HomePage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [featuredArticles, setFeaturedArticles] = useState([]);

  // Charger les 3 articles spécifiques pour la page d'accueil
  React.useEffect(() => {
    const fetchArticles = async () => {
      try {
        // Slugs des articles à afficher
        const featuredSlugs = [
          'marketing-reseau',
          'bien-demarrer-son-mlm',
          'outils-marketing-indispensables'
        ];
        
        // Charger chaque article par son slug
        const articlesPromises = featuredSlugs.map(slug => 
          axios.get(`${API}/articles/${slug}`).catch(err => null)
        );
        
        const articlesResponses = await Promise.all(articlesPromises);
        const articles = articlesResponses
          .filter(response => response !== null)
          .map(response => response.data);
        
        setFeaturedArticles(articles);
      } catch (error) {
        console.error('Error fetching articles:', error);
      }
    };
    fetchArticles();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, { name, email });
      // Redirect to thank you page
      window.location.href = '/merci';
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app">
      {/* Navigation */}
      <nav className="nav">
        <div className="nav-container">
          <div className="nav-logo" style={{cursor: 'pointer'}} onClick={() => window.location.href = '/'}>
            <span className="logo-text">AmsWeb</span>
            <span className="logo-subtitle">Aide MultiServices du Web</span>
          </div>
          <div className="nav-links">
            <a href="#formations" className="nav-link">Formations</a>
            <a href="#methode" className="nav-link">Notre Méthode</a>
            <Link to="/blog" className="nav-link">Blog</Link>
            <Link to="/formations-outils" className="nav-link">Nos Ressources</Link>
            <a href="#contact" className="nav-link nav-cta">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            Salut et Bienvenue 😉<br />
            <span className="hero-highlight">Développe Enfin Ton MLM</span>
          </h1>
          <p className="hero-subtitle">
            Apprends le métier de <strong>Networker</strong> avec des formations à jour,
            des outils efficaces et un accompagnement régulier.
          </p>
          <div className="hero-cta">
            <a href="#formations" className="btn btn-primary" data-testid="hero-cta-formations">
              Découvrir les Formations 🚀
            </a>
            <a href="#methode" className="btn btn-secondary" data-testid="hero-cta-methode">
              Notre Stratégie Unique
            </a>
          </div>
          <div className="hero-badges">
            <div className="badge">
              <span className="badge-icon">✅</span>
              <span>Formations à jour</span>
            </div>
            <div className="badge">
              <span className="badge-icon">🎯</span>
              <span>Stratégie gagnante</span>
            </div>
            <div className="badge">
              <span className="badge-icon">🤝</span>
              <span>Accompagnement personnalisé</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="container">
          <h2 className="section-title">Tu ne sais pas comment parrainer ? 🤔</h2>
          <div className="problem-grid">
            <div className="problem-card">
              <div className="problem-icon">😰</div>
              <h3>Pas de résultats</h3>
              <p>Tu travailles dur mais tu n'arrives pas à recruter ou vendre efficacement</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">📉</div>
              <h3>Techniques dépassées</h3>
              <p>Les vieilles méthodes ne marchent plus dans le monde digital d'aujourd'hui</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">😔</div>
              <h3>Seul et perdu</h3>
              <p>Ta upline n'a pas le temps de t'accompagner comme tu le mérites</p>
            </div>
          </div>
          <div className="problem-cta">
            <p className="problem-text">
              <strong>Si tu veux réussir, tu vas devoir apprendre le métier de Networker...</strong><br />
              Et si c'est ok pour toi, on peut t'aider.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section" id="formations">
        <div className="container">
          <div className="solution-content">
            <div className="solution-text">
              <h2 className="section-title">
                L'équipe AmsWeb te propose une <span className="highlight">stratégie UNIQUE</span>
              </h2>
              <p className="solution-description">
                On est en lien avec un <strong>PRO</strong> dans le domaine du MLM.
                On te propose une méthode que <strong>personne d'autre</strong> ne propose.
              </p>
              <div className="solution-features">
                <div className="feature-item">
                  <span className="feature-icon">👉</span>
                  <span><strong>Formations à jour</strong> - Les dernières techniques qui marchent vraiment</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">👉</span>
                  <span><strong>Outils pour réussir</strong> - Des ressources pratiques et immédiatement applicables</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">👉</span>
                  <span><strong>Suivi régulier</strong> - Un accompagnement continu pour ton succès</span>
                </div>
              </div>
              <a href="#contact" className="btn btn-large" data-testid="solution-cta">
                Je veux plus d'infos 💪
              </a>
            </div>
            <div className="solution-image">
              <img 
                src="https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?crop=entropy&cs=srgb&fm=jpg&q=85" 
                alt="Formation MLM en ligne"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-section" id="methode">
        <div className="container">
          <h2 className="section-title">Comment ça marche ? 🎯</h2>
          <p className="section-subtitle">3 étapes simples pour transformer ton MLM</p>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Tu te formes</h3>
              <p>Accède à des formations complètes et à jour sur les techniques de networking modernes</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Tu appliques</h3>
              <p>Utilise nos outils et notre stratégie unique pour démarrer immédiatement</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Tu réussis</h3>
              <p>Développe ton réseau, parraine efficacement et atteins ton indépendance financière</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="why-section">
        <div className="container">
          <h2 className="section-title">Pourquoi choisir AmsWeb ? 🌟</h2>
          <div className="why-grid">
            <div className="why-card">
              <div className="why-image">
                <img 
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Équipe gagnante"
                  loading="lazy"
                />
              </div>
              <h3>Tu as le droit de réussir autant que ta upline</h3>
              <p>On croit fermement que tout le monde mérite les mêmes chances de succès. Avec les bonnes formations et le bon accompagnement, tu peux atteindre et même dépasser les résultats de ta upline.</p>
            </div>
            <div className="why-card">
              <h3>Une stratégie UNIQUE sur le marché</h3>
              <p>On est les <strong>SEULS</strong> à proposer cette méthode. Tu vas rentrer progressivement dans un nouveau monde du networking digital.</p>
            </div>
            <div className="why-card">
              <h3>Accompagnement personnalisé</h3>
              <p>Contrairement à ta upline qui n'a peut-être pas le temps, nous sommes là pour toi régulièrement.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section className="blog-section" id="blog">
        <div className="container">
          <h2 className="section-title">Derniers articles du blog 📝</h2>
          <p className="section-subtitle">Toutes les astuces et innovations pour être au Top sur le Net</p>
          <div className="blog-grid">
            {featuredArticles.length > 0 ? (
              featuredArticles.map((article, index) => (
                <div className="blog-card" key={article.id}>
                  <div className="blog-image">
                    <img 
                      src={
                        index === 0 
                          ? "https://images.unsplash.com/photo-1515169067868-5387ec356754?crop=entropy&cs=srgb&fm=jpg&q=85"
                          : index === 1
                          ? "https://images.unsplash.com/photo-1501504905252-473c47e087f8?crop=entropy&cs=srgb&fm=jpg&q=85"
                          : "https://images.unsplash.com/photo-1675716921224-e087a0cca69a?crop=entropy&cs=srgb&fm=jpg&q=85"
                      }
                      alt={article.title}
                      loading="lazy"
                    />
                  </div>
                  <div className="blog-content">
                    <span className="blog-category">{article.category || 'MLM'}</span>
                    <h3>{article.title}</h3>
                    <p>{article.excerpt || 'Découvrez cet article passionnant sur le marketing de réseau...'}</p>
                    <Link to={`/blog/${article.slug}`} className="blog-link">Lire l'article →</Link>
                  </div>
                </div>
              ))
            ) : (
              // Articles par défaut si le chargement échoue
              <>
                <div className="blog-card">
                  <div className="blog-image">
                    <img 
                      src="https://images.unsplash.com/photo-1515169067868-5387ec356754?crop=entropy&cs=srgb&fm=jpg&q=85" 
                      alt="Marketing de réseau"
                      loading="lazy"
                    />
                  </div>
                  <div className="blog-content">
                    <span className="blog-category">Le Marketing de Réseau</span>
                    <h3>Bien démarrer son MLM</h3>
                    <p>Découvre les premières étapes essentielles pour lancer ton activité MLM sur de bonnes bases...</p>
                    <Link to="/blog" className="blog-link">Voir le blog →</Link>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="blog-cta">
            <button onClick={() => navigate('/blog')} className="btn btn-secondary" data-testid="blog-cta">Voir tous les articles</button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" id="contact">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Prêt à transformer ton MLM ? 🚀</h2>
            <p className="cta-subtitle">
              Laisse-nous tes coordonnées et découvre notre stratégie unique.<br />
              Tu vas recevoir plus d'informations sur nos formations et notre méthode.
            </p>
            {showSuccess && (
              <div className="success-message" data-testid="success-message">
                ✅ Merci ! On te contacte très bientôt.
              </div>
            )}
            <form className="cta-form" onSubmit={handleSubmit} data-testid="contact-form">
              <input 
                type="text" 
                placeholder="Ton prénom"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="form-input"
                data-testid="contact-form-name"
              />
              <input 
                type="email" 
                placeholder="Ton email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-input"
                data-testid="contact-form-email"
              />
              <button 
                type="submit" 
                className="btn btn-primary btn-large"
                disabled={isSubmitting}
                data-testid="contact-form-submit"
              >
                {isSubmitting ? 'Envoi...' : 'Je veux réussir mon MLM 💪'}
              </button>
            </form>
            <p className="cta-note">🔒 Tes données sont 100% sécurisées</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>AmsWeb</h3>
              <p>Aide MultiServices du Web</p>
              <p className="footer-tagline">Toutes les astuces pour développer ton MLM et être au Top sur le Net</p>
            </div>
            <div className="footer-section">
              <h4>Navigation</h4>
              <ul className="footer-links">
                <li><a href="#formations">Formations</a></li>
                <li><a href="#methode">Notre Méthode</a></li>
                <li><a href="#blog">Blog</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Contact</h4>
              <p>✉️ <a href="mailto:contact@amsweb.fr">contact@amsweb.fr</a></p>
              <p className="footer-fred">Avec Fred d'AmsWeb</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 AmsWeb.fr - Tous droits réservés</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/merci" element={<ThankYou />} />
        <Route path="/formations-outils" element={<Formations />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/contact" element={<ContactRedirect />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/articles" element={<AdminDashboard />} />
        <Route path="/admin/articles/create" element={<AdminCreateArticle />} />
        <Route path="/admin/articles/edit/:slug" element={<AdminEditArticle />} />
      </Routes>
    </BrowserRouter>
  );
}

export default AppRouter;